import {
  Alert,
  Button,
  CircularProgress,
  ExpandablePanel,
  RefreshIcon,
  TextField,
  Typography,
} from "@firecms/ui";
import type { ReactNode } from "react";

import type { PanelState } from "../types";
import { formatTime } from "../utils";

const ResultCount = ({ count }: { count: number }) => (
  <span className="ml-2 text-xs font-normal bg-surface-100 dark:bg-surface-800 text-text-secondary dark:text-text-secondary-dark px-2 py-0.5 rounded-full">
    {count}
  </span>
);

const PanelTitle = ({ title, count }: { title: string; count?: number }) => (
  <div className="flex items-center">
    <Typography variant="subtitle2">{title}</Typography>
    {count !== undefined && <ResultCount count={count} />}
  </div>
);

const PanelStatus = ({ state, onRetry }: { state: PanelState<unknown>; onRetry: () => void }) => {
  if (state.loading) {
    return (
      <div className="flex items-center gap-2 py-1">
        <CircularProgress size="small" />
        <Typography variant="caption" color="secondary">
          Loading...
        </Typography>
      </div>
    );
  }
  if (state.error) {
    return (
      <Alert
        color="error"
        action={
          <Button variant="text" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {state.error}
      </Alert>
    );
  }
  if (state.fetchedAt) {
    return (
      <Typography variant="caption" color="secondary">
        Last fetched at {formatTime(state.fetchedAt)}
      </Typography>
    );
  }
  return null;
};

export type PanelToolbarProps = {
  search: string;
  onSearchChange: (v: string) => void;
  fetchLabel: string;
  onFetch: () => void;
  disabled: boolean;
  state: PanelState<unknown>;
};

export const PanelToolbar = ({
  search,
  onSearchChange,
  fetchLabel,
  onFetch,
  disabled,
  state,
}: PanelToolbarProps) => (
  <div className="flex items-center gap-2 flex-wrap">
    {state.results !== null && (
      <div className="flex-1 min-w-40">
        <TextField
          size="small"
          placeholder="Filter results..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
    )}
    <div className="ml-auto flex items-center gap-3">
      <PanelStatus state={state} onRetry={onFetch} />
      <Button
        variant="filled"
        size="small"
        onClick={onFetch}
        disabled={disabled || state.loading}
        startIcon={
          state.loading ? <CircularProgress size="smallest" /> : <RefreshIcon size="small" />
        }
      >
        {state.results ? "Refresh" : fetchLabel}
      </Button>
    </div>
  </div>
);

export type PanelProps = {
  title: string;
  count?: number;
  disabled: boolean;
  disabledReason?: string;
  queryFilters?: ReactNode;
  toolbar: ReactNode;
  children: ReactNode;
};

export const Panel = ({
  title,
  count,
  disabled,
  disabledReason,
  queryFilters,
  toolbar,
  children,
}: PanelProps) => (
  <ExpandablePanel
    title={<PanelTitle title={title} count={count} />}
    initiallyExpanded
    className="border border-surface-accent-200 dark:border-surface-700 rounded-lg"
  >
    <div className="flex flex-col gap-3 p-3">
      {disabled && disabledReason && <Alert color="warning">{disabledReason}</Alert>}
      {queryFilters && <div>{queryFilters}</div>}
      {toolbar}
      {children}
    </div>
  </ExpandablePanel>
);
